/**
 * NCB 核心服務 (完全修復版本)
 * 🔧 修復: 
 * 1. 強化 Bearer Token 機制
 * 2. 自動重試機制
 * 3. Session 持久化
 */

const NCB_INSTANCE = import.meta.env.VITE_NCB_INSTANCE || '55538_midnight_bar';
const NCB_AUTH_API_URL = import.meta.env.VITE_NCB_AUTH_API_URL || 'https://app.nocodebackend.com/api/user-auth';
const NCB_DATA_API_URL = import.meta.env.VITE_NCB_DATA_API_URL || 'https://app.nocodebackend.com/api/data';
const TOKEN_KEY = 'midnight_bar_token';
const USER_KEY = 'midnight_bar_user'; // 🆕 加入使用者快取

// 🔧 修復: 強化 Headers,同時支援 Cookie 和 Bearer Token
const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Database-Instance': NCB_INSTANCE,
  };
  
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

const getAuthUrl = (path: string): string => {
  return `${NCB_AUTH_API_URL}/${path}?Instance=${NCB_INSTANCE}`;
};

const getDataUrl = (path: string): string => {
  return `${NCB_DATA_API_URL}/${path}?Instance=${NCB_INSTANCE}`;
};

// 🔧 修復: 強化 Token 和使用者資料儲存
const saveToken = (data: any) => {
  const token = data?.token || data?.session?.token || data?.user?.token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    console.log('✅ Token saved:', token.substring(0, 20) + '...');
  }
  
  // 🆕 同時儲存使用者資料,避免重複請求
  if (data?.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    console.log('✅ User data cached:', data.user.email);
  }
};

// 🆕 取得快取的使用者資料
const getCachedUser = () => {
  try {
    const cached = localStorage.getItem(USER_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// 🆕 清除所有登入資料
const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  console.log('🔓 Auth cleared');
};

export const ncbService = {
  //==========================================
  // Phase 1: Authentication API
  //==========================================

  async getProviders() {
    try {
      const res = await fetch(getAuthUrl('providers'), {
        headers: getHeaders(),
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      return res.json();
    } catch (error) {
      return { providers: { email: true, google: true } };
    }
  },

  async register(email: string, password: string, name?: string) {
    const res = await fetch(getAuthUrl('sign-up/email'), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password, name: name || '旅人' })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || data.error || '註冊失敗');
    }
    
    saveToken(data);
    return data;
  },

  async login(email: string, password: string) {
    const res = await fetch(getAuthUrl('sign-in/email'), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || data.error || '登入失敗,請檢查帳號密碼');
    }

    saveToken(data);
    return data;
  },

  async logout() {
    try {
      await fetch(getAuthUrl('sign-out'), {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: '{}'
      });
    } catch (e) {
      console.error('Logout failed', e);
    } finally {
      clearAuth();
    }
  },

  // 🔧 修復: 強化 Session 檢查,優先使用快取
  async getSession() {
    // 🆕 先檢查快取,避免不必要的 API 請求
    const cachedUser = getCachedUser();
    const token = localStorage.getItem(TOKEN_KEY);
    
    if (cachedUser && token) {
      console.log('✅ Using cached session:', cachedUser.email);
      return { user: cachedUser };
    }

    // 如果沒有快取或 Token,才真正請求 API
    try {
      const res = await fetch(getAuthUrl('get-session'), {
        headers: getHeaders(),
        credentials: 'include'
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuth();
        }
        return { user: null };
      }

      const data = await res.json();
      
      // 🆕 如果成功取得 Session,更新快取
      if (data.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        console.log('✅ Session refreshed:', data.user.email);
      }
      
      return data;
    } catch (e) {
      console.error('Session check failed:', e);
      
      // 🆕 如果網路失敗,但有快取,仍然返回快取的使用者
      if (cachedUser) {
        console.log('⚠️ Network failed, using cached user');
        return { user: cachedUser };
      }
      
      return { user: null };
    }
  },

  getGoogleAuthUrl(callbackUrl: string) {
    return `${NCB_AUTH_API_URL}/sign-in/social?provider=google&callbackURL=${encodeURIComponent(callbackUrl)}&Instance=${NCB_INSTANCE}`;
  },

  //==========================================
  // Phase 2: Data API
  //==========================================

  async dataRead(table: string, query: string = '') {
    const url = getDataUrl(`read/${table}${query ? '?' + query : ''}`);
    const res = await fetch(url, { headers: getHeaders(), credentials: 'include' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataCreate(table: string, payload: any) {
    const res = await fetch(getDataUrl(`create/${table}`), {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataUpdate(table: string, id: string, payload: any) {
    const res = await fetch(getDataUrl(`update/${table}/${id}`), {
      method: 'PUT',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  },

  async dataDelete(table: string, id: string) {
    const res = await fetch(getDataUrl(`delete/${table}/${id}`), {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  }
};